import { validCnpj, validCpf } from 'services/validDocument'
import { mask } from 'utils/mask'
import { onlyNumbers } from 'utils/string'

export type PixKeyType = 'CPF' | 'CNPJ' | 'PHONE' | 'EMAIL' | 'EVP'

export const PIX_KEY_TYPES: { label: string; value: PixKeyType }[] = [
  { label: 'CPF', value: 'CPF' },
  { label: 'CNPJ', value: 'CNPJ' },
  { label: 'E-mail', value: 'EMAIL' },
  { label: 'Telefone', value: 'PHONE' },
  { label: 'Chave aleatória (EVP)', value: 'EVP' },
]

const norm = (s: string) =>
  s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

export function normalizePixType(label: string): PixKeyType | '' {
  switch (norm(label)) {
    case 'celular':
    case 'telefone':
    case 'phone':
    case 'tel':
      return 'PHONE'
    case 'evp':
    case 'aleatoria':
    case 'chave_aleatoria':
    case 'chave aleatoria':
    case 'random':
      return 'EVP'
    case 'cpf':
      return 'CPF'
    case 'cnpj':
      return 'CNPJ'
    case 'email':
    case 'e-mail':
    case 'e_mail':
      return 'EMAIL'
    default:
      return ''
  }
}

const phoneLocal = (value: string): string => {
  const digits = onlyNumbers(value)
  return digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits
}

const EVP_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Espelha a ordem de unMaskPixKey do back-end: CPF → CNPJ → PHONE (mascarado/+55) → EVP → EMAIL.
export function detectPixType(value: string): PixKeyType | '' {
  const v = value.trim()
  const digits = onlyNumbers(v)
  if (digits.length === 11 && validCpf(digits)) return 'CPF'
  if (digits.length === 14 && validCnpj(digits)) return 'CNPJ'
  if (/^\(\d{2}\)\s?\d{4,5}-?\d{4}$/.test(v) || /^\+55\d{10,11}$/.test(v)) return 'PHONE'
  if (EVP_RE.test(v)) return 'EVP'
  if (EMAIL_RE.test(v)) return 'EMAIL'
  return ''
}

export function validatePixKey(value: string, type: PixKeyType): string | null {
  const digits = onlyNumbers(value)
  switch (type) {
    case 'CPF':
      return digits.length === 11 && validCpf(digits) ? null : 'CPF inválido'
    case 'CNPJ':
      return digits.length === 14 && validCnpj(digits) ? null : 'CNPJ inválido'
    case 'PHONE': {
      const local = phoneLocal(value)
      return local.length >= 10 && local.length <= 11 ? null : 'Telefone inválido'
    }
    case 'EMAIL':
      return EMAIL_RE.test(value.trim()) ? null : 'E-mail inválido'
    case 'EVP':
      return EVP_RE.test(value.trim()) ? null : 'Chave aleatória inválida'
  }
}

export function pixKeyToRaw(value: string, type: PixKeyType): string {
  if (!value) return ''
  switch (type) {
    case 'CPF':
    case 'CNPJ':
      return onlyNumbers(value)
    case 'PHONE':
      return `+55${phoneLocal(value)}`
    case 'EMAIL':
    case 'EVP':
      return value.trim().toLowerCase()
  }
}

export function applyPixKeyMask(value: string, type: string): string {
  const digits = onlyNumbers(value)
  switch (type) {
    case 'CPF':
      return mask(digits, '999.999.999-99')
    case 'CNPJ':
      return mask(digits, '99.999.999/9999-99')
    case 'PHONE':
      return mask(digits, digits.length > 10 ? '(99) 99999-9999' : '(99) 9999-9999')
    default:
      return value
  }
}
